import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

/* switch_account.html (CEO, 2 Nov 2026) — exact behaviour: one login,
   switch between CEO · Revitalise Keratin, Hair 305 London (£) and
   Hair 305 Lagos (₦) with no logging out. The phone remembers the
   last account; a plum bar says "You're in [salon] as CEO" with
   "← Back to CEO". Server-side the switch is founder-only. */
const API = process.env.REACT_APP_BACKEND_URL;
export const ACC_KEY = 'rvk_acc';
const CEO_TOKEN_KEY = 'rvk_ceo_home_token';

export function ceoBarState() {
  try {
    const acc = localStorage.getItem(ACC_KEY);
    const home = localStorage.getItem(CEO_TOKEN_KEY);
    if (acc && acc !== 'ceo' && home) {
      return { label: acc === 'lagos' ? 'Hair 305 Lagos' : 'Hair 305 London' };
    }
  } catch { /* no state */ }
  return null;
}

export function backToCeo() {
  try {
    const home = localStorage.getItem(CEO_TOKEN_KEY);
    if (!home) return;
    localStorage.setItem('rk_token', home);
    localStorage.removeItem(CEO_TOKEN_KEY);
    localStorage.setItem(ACC_KEY, 'ceo');
  } catch { /* no state */ }
  window.location.href = '/dashboard';
}

export const CeoAccountSwitch = ({ dark }) => {
  const { user, token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const inSalon = !!ceoBarState();

  useEffect(() => {
    if (!token) return undefined;
    let dead = false;
    if (user?.role !== 'founder') {
      if (ceoBarState()) return undefined;
      // ceo_login_hotfix: the CEO's London/Lagos logins open on CEO · Revitalise
      (async () => {
        try {
          const r = await fetch(`${API}/api/ceo-switch/am-i-linked`, { headers: { Authorization: `Bearer ${token}` } });
          if (!r.ok || dead) return;
          const d = await r.json();
          if (!d.linked) return;
          const r2 = await fetch(`${API}/api/ceo-switch/to-ceo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
          if (!r2.ok || dead) return;
          const d2 = await r2.json();
          localStorage.setItem('rk_token', d2.token);
          localStorage.removeItem(CEO_TOKEN_KEY);
          localStorage.setItem(ACC_KEY, 'ceo');
          window.location.href = '/dashboard';
        } catch { /* stays on the salon login */ }
      })();
      return () => { dead = true; };
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/ceo-switch/accounts`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const d = await r.json(); if (!dead) setAccounts(d.accounts || []); }
      } catch { /* hidden */ }
    })();
    return () => { dead = true; };
  }, [token, user?.role]);

  if (user?.role !== 'founder' && !inSalon) return null;

  const pick = async (key) => {
    if (key === 'ceo') { backToCeo(); return; }
    try {
      const r = await fetch(`${API}/api/ceo-switch/switch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: key }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Switch failed');
      localStorage.setItem(CEO_TOKEN_KEY, localStorage.getItem('rk_token') || token);
      localStorage.setItem('rk_token', d.token);
      localStorage.setItem(ACC_KEY, key);
      window.location.href = '/dashboard';
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <button type="button" data-testid="ceo-account-switch-btn" onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none',
                 border: 'none', cursor: 'pointer', minHeight: 44, color: dark ? '#F8F4F1' : '#2B1F27',
                 font: '600 15px Lexend, Arial, sans-serif' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" /></svg>
        Switch account
      </button>
      {open && (
        <div data-testid="ceo-account-sheet" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 6 }}>
          {accounts.map((a) => (
            <button key={a.key} type="button" data-testid={`acc-${a.key}`} disabled={!a.linked}
              onClick={() => pick(a.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', minHeight: 52,
                       borderRadius: 14, border: '1px solid rgba(217,190,142,.4)', padding: '8px 12px',
                       background: 'rgba(255,255,255,.06)', color: dark ? '#F8F4F1' : '#2B1F27',
                       cursor: a.linked ? 'pointer' : 'not-allowed', opacity: a.linked ? 1 : 0.5 }}>
              <span style={{ width: 34, height: 34, borderRadius: 999, background: '#D9BE8E', color: '#291525',
                             display: 'grid', placeItems: 'center', font: '700 12px Lexend' }}>
                {a.key === 'ceo' ? 'JC' : 'H'}</span>
              <span style={{ minWidth: 0 }}>
                <b style={{ display: 'block', fontSize: 15 }}>{a.name}</b>
                <small style={{ opacity: 0.75 }}>{a.linked ? a.sub : 'Not linked yet'}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const CeoAccountBar = () => {
  const st = ceoBarState();
  if (!st) return null;
  return (
    <div data-testid="ceo-account-bar" style={{ position: 'sticky', top: 0, zIndex: 60, background: '#291525',
      color: '#F8F4F1', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 14px', font: '600 14px Lexend, Arial, sans-serif' }}>
      <span>You&apos;re in {st.label} as CEO</span>
      <button type="button" data-testid="back-to-ceo-btn" onClick={backToCeo}
        style={{ background: 'none', border: '1px solid #D9BE8E', color: '#D9BE8E', borderRadius: 999,
                 minHeight: 36, padding: '0 14px', cursor: 'pointer', font: 'inherit' }}>
        ← Back to CEO
      </button>
    </div>
  );
};
